import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, InputHTMLAttributes, ReactElement } from 'react';
import { OperationsClientError, operationsClient, operationsErrorMessage } from './client';
import type { OperationsClient } from './client';
import type { OperationsSettingsValue, OperationsSnapshot, OperationsUpdate } from './contracts';
import { OperationsElement as E } from './RegisteredElement';
import styles from './operations.module.css';
import { loadFeatureFlags } from '../../core/featureFlags';
import { AppIcon } from '../../ui/AppIcon';
import { AUTOMATION_PROMPT_DEFAULTS } from '../../../server/src/modules/operations/automationPrompts.js';
import { AUTOMATION_PROMPT_FIELDS } from './automationPromptFields';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  name: string; label: string; value: string | number; onChange: (value: string) => void;
}
function Field({ name, label, onChange, ...props }: FieldProps): ReactElement {
  const id = useId();
  return <E as="label" meta={`${name}.label`} htmlFor={id} className={styles.field}>{label}
    <E as="input" meta={name} id={id} {...props} onChange={(event) => onChange(event.currentTarget.value)} />
  </E>;
}
function Toggle({ name, label, value, disabled, onChange }: { name: string; label: string; value: boolean; disabled: boolean; onChange: (value: boolean) => void }): ReactElement {
  const id = useId();
  return <E as="label" meta={`${name}.label`} htmlFor={id} className={styles.toggle}>
    <E as="input" meta={name} id={id} type="checkbox" checked={value} disabled={disabled} onChange={(event) => onChange(event.currentTarget.checked)} />{label}
  </E>;
}
export interface OperationsSettingsProps { client?: OperationsClient; onSaved?: () => void; }
const INITIATIVE_MODES = [
  ['user_priority', '主用户要求'],
  ['character_priority', '主角色依据自身设定决定'],
  ['balanced', '角色决定或用户要求'],
] as const;

export function OperationsSettings({ client = operationsClient, onSaved }: OperationsSettingsProps = {}): ReactElement {
  const [snapshot, setSnapshot] = useState<OperationsSnapshot>();
  const [draft, setDraft] = useState<OperationsSettingsValue>();
  const [smtpPassword, setSmtpPassword] = useState('');
  const [paymentPrivateKey, setPaymentPrivateKey] = useState('');
  const [paymentPublicKey, setPaymentPublicKey] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const current = useRef(0);
  const lock = useRef(false);
  const tlsId = useId(); const typeId = useId();
  const load = useCallback(async (signal?: AbortSignal) => {
    const revision = ++current.current;
    setBusy(true); setError(''); setNotice(''); setSmtpPassword(''); setPaymentPrivateKey(''); setPaymentPublicKey('');
    try {
      const result = await client.load(signal);
      if (revision === current.current) { setSnapshot(result); setDraft(result.settings); }
    } catch (failure) {
      if (revision === current.current && !signal?.aborted) { setError(operationsErrorMessage(failure)); setSnapshot(undefined); setDraft(undefined); }
    } finally { if (revision === current.current) setBusy(false); }
  }, [client]);
  useEffect(() => {
    const abort = new AbortController(); void load(abort.signal);
    const generation = current.current;
    return () => { current.current = generation + 1; abort.abort(); };
  }, [load]);
  function smtp<K extends keyof OperationsSettingsValue['smtp']>(key: K, value: OperationsSettingsValue['smtp'][K]): void {
    setDraft((previous) => previous ? { ...previous, smtp: { ...previous.smtp, [key]: value } } : previous);
  }
  function payment<K extends keyof OperationsSettingsValue['payment']>(key: K, value: OperationsSettingsValue['payment'][K]): void {
    setDraft((previous) => previous ? { ...previous, payment: { ...previous.payment, [key]: value } } : previous);
  }
  function moments(key: keyof OperationsSettingsValue['moments'], value: string): void {
    setDraft((previous) => previous ? { ...previous, moments: { ...previous.moments, [key]: Number(value) } } : previous);
  }
  async function save(event: FormEvent): Promise<void> {
    event.preventDefault(); if (lock.current || busy || !draft) return;
    lock.current = true; setBusy(true); setError(''); setNotice('');
    const revision = current.current;
    const update: OperationsUpdate = { settings: draft, ...(smtpPassword ? { smtpPassword } : {}), ...(paymentPrivateKey.trim() ? { paymentPrivateKey: paymentPrivateKey.trim() } : {}), ...(paymentPublicKey.trim() ? { paymentPublicKey: paymentPublicKey.trim() } : {}) };
    try {
      await client.save(update);
      if (revision !== current.current) return;
      setSmtpPassword(''); setPaymentPrivateKey(''); setPaymentPublicKey('');
      const result = await client.load();
      if (revision !== current.current) return;
      setSnapshot(result); setDraft(result.settings); setNotice('运维设置已保存。'); void loadFeatureFlags(true); onSaved?.();
    } catch (failure) {
      if (revision !== current.current) return;
      setError(operationsErrorMessage(failure));
      if (failure instanceof OperationsClientError && [401, 403].includes(failure.status)) { setSnapshot(undefined); setDraft(undefined); setSmtpPassword(''); setPaymentPrivateKey(''); setPaymentPublicKey(''); }
    } finally { lock.current = false; if (revision === current.current) setBusy(false); }
  }
  const secretDisabled = busy || !snapshot?.encryptionConfigured;
  return <E meta="root" as="section" className={styles.root} aria-busy={busy}>
    <E meta="title" as="h2">运维设置</E>
    <E meta="readiness" as="p" role="status">{busy && !snapshot ? '正在加载设置' : snapshot ? `邮件：${snapshot.mailReady ? '已就绪' : '未就绪'} · 易支付：${snapshot.paymentReady ? '已就绪' : '未就绪'}` : '运维设置暂不可用'}</E>
    {snapshot ? <E meta="credentials" as="p" className={styles.muted}>SMTP 密码：{snapshot.smtpPasswordConfigured ? '已配置' : '未配置'} · RSA 私钥：{snapshot.paymentPrivateKeyConfigured ? '已配置' : '未配置'} · RSA 公钥：{snapshot.paymentPublicKeyConfigured ? '已配置' : '未配置'} · 加密密钥：{snapshot.encryptionConfigured ? '已配置' : '未配置'}</E> : null}
    <E meta="refresh" as="button" type="button" disabled={busy} onClick={() => void load()}>刷新</E>
    {draft ? <E meta="form" as="form" onSubmit={(event) => void save(event)}>
      <E meta="registration" as="fieldset" disabled={busy}>
        <E meta="registrationTitle" as="legend">注册</E>
        <Toggle name="registration.enabled" label="允许注册" value={draft.registration.enabled} disabled={busy} onChange={(enabled) => setDraft((previous) => previous ? { ...previous, registration: { enabled } } : previous)} />
      </E>
      <E meta="features" as="fieldset" disabled={busy}>
        <E meta="featuresTitle" as="legend">功能开关</E>
        <Toggle
          name="features.autoMemory"
          label="自动记忆（开启后用户端才显示入口）"
          value={draft.features.autoMemory}
          disabled={busy}
          onChange={(autoMemory) => setDraft((previous) => previous ? { ...previous, features: { ...previous.features, autoMemory } } : previous)}
        />
        <Toggle
          name="features.showContextInjection"
          label="对话显示已注入提示"
          value={draft.features.showContextInjection}
          disabled={busy}
          onChange={(showContextInjection) => setDraft((previous) => previous ? { ...previous, features: { ...previous.features, showContextInjection } } : previous)}
        />
        <Toggle
          name="features.showReasoning"
          label="对话显示思考过程"
          value={draft.features.showReasoning}
          disabled={busy}
          onChange={(showReasoning) => setDraft((previous) => previous ? { ...previous, features: { ...previous.features, showReasoning } } : previous)}
        />
      </E>
      {(['imageInitiative', 'ttsInitiative'] as const).map((kind) => {
        const label = kind === 'imageInitiative' ? '生图积极性' : '语音 TTS 积极性';
        const config = draft.features[kind];
        return <E key={kind} meta={`features.${kind}`} as="fieldset" disabled={busy}>
          <E meta={`features.${kind}.title`} as="legend">{label}</E>
          <Toggle name={`features.${kind}.enabled`} label={`启用${label}`} value={config.enabled} disabled={busy}
            onChange={(enabled) => setDraft((previous) => previous ? { ...previous, features: { ...previous.features, [kind]: { ...previous.features[kind], enabled } } } : previous)} />
          {INITIATIVE_MODES.map(([mode, modeLabel]) => <E key={mode} meta={`features.${kind}.${mode}.label`} as="label" className={styles.field}>
            {`${label} · ${modeLabel}`}
            <E meta={`features.${kind}.${mode}`} as="textarea" rows={5} maxLength={10000} value={config.prompts[mode]}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setDraft((previous) => previous ? { ...previous, features: { ...previous.features, [kind]: { ...previous.features[kind], prompts: { ...previous.features[kind].prompts, [mode]: value } } } } : previous);
              }} />
          </E>)}
        </E>;
      })}
      <E meta="moments" as="fieldset" disabled={busy}>
        <E meta="momentsTitle" as="legend">朋友圈离线设置</E>
        <Field name="moments.retentionHours" label="朋友圈保留时长（小时）" value={draft.moments.retentionHours} type="number" min={1} max={Number.MAX_SAFE_INTEGER} step={1} required onChange={(value) => moments('retentionHours', value)} />
        <Field name="moments.configIdleRetentionHours" label="生成配置闲置保留时长（小时）" value={draft.moments.configIdleRetentionHours} type="number" min={1} max={Number.MAX_SAFE_INTEGER} step={1} required onChange={(value) => moments('configIdleRetentionHours', value)} />
        <Field name="moments.minDecisionIntervalMinutes" label="自主判断最短间隔（分钟）" value={draft.moments.minDecisionIntervalMinutes} type="number" min={1} max={1440} step={1} required onChange={(value) => moments('minDecisionIntervalMinutes', value)} />
      </E>
      <E meta="features.automationPrompts" as="fieldset" disabled={busy}>
        <E meta="features.automationPrompts.title" as="legend">自动化提示词</E>
        <E meta="features.automationPrompts.restore" as="button" type="button" className={styles.restorePrompts}
          onClick={() => setDraft((previous) => previous ? { ...previous, features: { ...previous.features, automationPrompts: { ...AUTOMATION_PROMPT_DEFAULTS } } } : previous)}>
          <AppIcon name="refresh" size={18} />恢复自动化默认提示词
        </E>
        {AUTOMATION_PROMPT_FIELDS.map(([key, label, rows]) => <E key={key} meta={`features.automationPrompts.${key}.label`} as="label" className={styles.field}>
          {label}
          <E meta={`features.automationPrompts.${key}`} as="textarea" rows={rows} maxLength={10000} spellCheck={false} value={draft.features.automationPrompts[key]}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setDraft((previous) => previous ? { ...previous, features: { ...previous.features, automationPrompts: { ...previous.features.automationPrompts, [key]: value } } } : previous);
            }} />
        </E>)}
      </E>
      <E meta="smtp" as="fieldset" disabled={busy}>
        <E meta="smtpTitle" as="legend">SMTP</E>
        <Toggle name="smtp.enabled" label="启用邮件发送" value={draft.smtp.enabled} disabled={busy} onChange={(value) => smtp('enabled', value)} />
        <Field name="smtp.host" label="SMTP 主机" value={draft.smtp.host} maxLength={253} required={draft.smtp.enabled} onChange={(value) => smtp('host', value)} />
        <Field name="smtp.port" label="SMTP 端口" type="number" value={draft.smtp.port} min={1} max={65535} step={1} required onChange={(value) => smtp('port', Number(value))} />
        <E meta="smtp.secure.label" as="label" htmlFor={tlsId} className={styles.field}>TLS 模式
          <E meta="smtp.secure" as="select" id={tlsId} value={draft.smtp.secure ? 'tls' : 'starttls'} onChange={(event) => smtp('secure', event.currentTarget.value === 'tls')}>
            <E meta="tlsOption" instance="starttls" as="option" value="starttls">强制 STARTTLS</E>
            <E meta="tlsOption" instance="tls" as="option" value="tls">隐式 TLS</E>
          </E>
        </E>
        <Field name="smtp.user" label="SMTP 用户名" value={draft.smtp.user} maxLength={254} autoComplete="off" onChange={(value) => smtp('user', value)} />
        <Field name="smtp.from" label="发件人邮箱" value={draft.smtp.from} type="email" maxLength={254} required={draft.smtp.enabled} onChange={(value) => smtp('from', value)} />
        <Field name="smtpPassword" label="新的 SMTP 密码" type="password" value={smtpPassword} maxLength={4096} autoComplete="new-password" disabled={secretDisabled} onChange={setSmtpPassword} />
      </E>
      <E meta="payment" as="fieldset" disabled={busy}>
        <E meta="paymentTitle" as="legend">易支付</E>
        <Toggle name="payment.enabled" label="启用支付" value={draft.payment.enabled} disabled={busy} onChange={(value) => payment('enabled', value)} />
        <Field name="payment.gatewayUrl" label="网关 HTTPS 地址" type="url" value={draft.payment.gatewayUrl} maxLength={1024} required={draft.payment.enabled} onChange={(value) => payment('gatewayUrl', value)} />
        <Field name="payment.pid" label="商户号" value={draft.payment.pid} maxLength={32} pattern="[0-9]{1,32}" required={draft.payment.enabled} onChange={(value) => payment('pid', value)} />
        <Field name="payment.notifyUrl" label="异步通知地址" type="url" value={draft.payment.notifyUrl} maxLength={1024} required={draft.payment.enabled} onChange={(value) => payment('notifyUrl', value)} />
        <Field name="payment.returnUrl" label="同步跳转地址" type="url" value={draft.payment.returnUrl} maxLength={1024} required={draft.payment.enabled} onChange={(value) => payment('returnUrl', value)} />
        <E meta="payment.type.label" as="label" htmlFor={typeId} className={styles.field}>支付方式
          <E meta="payment.type" as="select" id={typeId} value={draft.payment.type} onChange={(event) => payment('type', event.currentTarget.value as OperationsSettingsValue['payment']['type'])}>
            {(['alipay', 'wxpay', 'qqpay'] as const).map((type) => <E meta="paymentOption" instance={type} key={type} as="option" value={type}>{({ alipay: '支付宝', wxpay: '微信支付', qqpay: 'QQ 钱包' })[type]}</E>)}
          </E>
        </E>
        <Field name="payment.minAmountCents" label="最小金额（分）" value={draft.payment.minAmountCents} type="number" min={1} max={100000000} step={1} required onChange={(value) => payment('minAmountCents', Number(value))} />
        <Field name="payment.orderTtlMinutes" label="订单有效期（分钟）" value={draft.payment.orderTtlMinutes} type="number" min={1} max={1440} step={1} required onChange={(value) => payment('orderTtlMinutes', Number(value))} />
        <Field name="payment.signType" label="签名方式（易支付 V2）" value={draft.payment.signType} disabled onChange={() => undefined} />
        <E meta="rsaHelp" as="p" className={styles.muted}>填写商户 RSA 私钥及支付平台 RSA 公钥，支持 PEM 或纯 Base64（2048–8192 位）。私钥用于下单签名，平台公钥用于回调验签；商户自己的公钥应上传到支付平台。留空保留已保存的密钥。</E>
        <E meta="paymentPrivateKey.label" as="label" className={styles.field}>商户 RSA 私钥
          <E meta="paymentPrivateKey" as="textarea" rows={5} value={paymentPrivateKey} maxLength={16384} autoComplete="off" spellCheck={false} disabled={secretDisabled} onChange={(event) => setPaymentPrivateKey(event.currentTarget.value)} />
        </E>
        <E meta="paymentPublicKey.label" as="label" className={styles.field}>支付平台 RSA 公钥
          <E meta="paymentPublicKey" as="textarea" rows={4} value={paymentPublicKey} maxLength={16384} autoComplete="off" spellCheck={false} disabled={secretDisabled} onChange={(event) => setPaymentPublicKey(event.currentTarget.value)} />
        </E>
      </E>
      <E meta="save" as="button" type="submit" disabled={busy}>保存设置</E>
    </E> : null}
    {error || notice ? <E meta="status" as="p" className={error ? styles.error : styles.muted} role={error ? 'alert' : 'status'}>{error || notice}</E> : null}
  </E>;
}
